import { useMutation, useQueryClient, useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '@/shared/lib/axios';
import { getClinics, type ClinicsListParams } from '@/services/admin/clinics';
import { ClinicsListResponse } from '@/services/admin/clinics/types';

// Define the type for the clinic creation form data, which I'll get from the form component.
interface CreateClinicFormData {
  site_type: string | null;
  service_type: 'A2' | 'R2' | 'BOTH' | null;
  clinic_number: string;
  available_from: string;
  available_to?: string | null;
  is_excluded_transfer: 0 | 1;
  is_none_disp_delegation_message?: 0 | 1;
  is_none_disp_delegation2_message?: 0 | 1;
  is_none_disp_delegation3_message?: 0 | 1;
  is_enable_checkin?: 0 | 1;
  checkin_available_from?: string | null;
  checkin_available_to?: string | null;
}

/**
 * API service function to create a new clinic.
 * @param site The site identifier (e.g., 'medix').
 * @param data The clinic form data.
 * @returns The response from the API.
 */
const createClinic = async (site: string, data: CreateClinicFormData) => {
  const response = await apiClient.post(`/admin/${site}/clinics`, data);
  return response.data;
};

const updateClinic = async (site: string, id: number, data: CreateClinicFormData) => {
  const response = await apiClient.put(`/admin/${site}/clinics/${id}`, data);
  return response.data;
};

const getClinic = async (site: string, id: number) => {
  const response = await apiClient.get(`/admin/${site}/clinics/${id}`);
  return response.data;
};

/**
 * React Query mutation hook for creating a clinic.
 */
export const useAdminCreateClinic = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ site, data }: { site: string; data: CreateClinicFormData }) =>
      createClinic(site, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'clinics'] });
      queryClient.invalidateQueries({
        queryKey: ['admin', variables.site, 'clinics', 'options'],
      });
    },
  });
};

/**
 * React Query mutation hook for updating a clinic.
 */
export const useAdminUpdateClinic = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ site, id, data }: { site: string; id: number; data: CreateClinicFormData }) =>
      updateClinic(site, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'clinics'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'clinics', variables.id] });
      queryClient.invalidateQueries({
        queryKey: ['admin', variables.site, 'clinics', 'options'],
      });
      queryClient.invalidateQueries({ queryKey: ['site-admin', 'clinics'] });
      queryClient.invalidateQueries({
        queryKey: ['clinic-detail', String(variables.id), variables.site],
      });
    },
  });
};

/**
 * React Query hook for fetching a single clinic.
 */
export const useAdminClinic = (site: string, id: number) => {
  return useQuery({
    queryKey: ['admin', 'clinics', id],
    queryFn: () => getClinic(site, id),
    enabled: !!site && !!id,
  });
};

/**
 * React Query infinite hook for fetching clinics list (e.g. for dropdowns with scroll).
 */
export const useAdminInfiniteClinicsList = (site: string | null, params?: ClinicsListParams) => {
  return useInfiniteQuery({
    queryKey: ['admin', 'clinics', 'infinite-list', site, params],
    queryFn: ({ pageParam = 1 }) => getClinics(site!, { ...params, page: pageParam as number }),
    getNextPageParam: (lastPage: ClinicsListResponse) => {
      if (lastPage.data.current_page < lastPage.data.last_page) {
        return lastPage.data.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!site,
  });
};

/**
 * React Query hook for fetching clinics list (e.g. for dropdowns).
 * Use this in Pages instead of calling getClinics directly.
 */
export const useAdminClinicsList = (site: string | null, params?: ClinicsListParams) => {
  return useQuery({
    queryKey: ['admin', 'clinics', 'list', site, params],
    queryFn: () => getClinics(site!, params ?? {}),
    enabled: !!site,
  });
};
